import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Alert,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
  InputAdornment,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import {
  FaPlus,
  FaBuilding,
  FaStore,
  FaEdit,
  FaTrash,
  FaExpandArrowsAlt,
  FaSearch,
  FaEllipsisV,
  FaTimes,
  FaLock,
} from "react-icons/fa";
import { MdExpandMore } from "react-icons/md";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  createStoreGroup,
  getStoreGroupsDetailed,
  getDropdownData,
  deleteStoreGroup,
  updateStoreGroup,
  selectGiftCertificate,
  resetGiftCertificate,
  GiftCertificateState,
} from "../slices/gift-certificate.slice";
import {
  getAdminSession,
  selectGetAdminSession,
} from "features/admin/presentation/slices/get-admin-session.slice";

export function StoreGroupsManagement() {
  const dispatch = useAppDispatch();
  const giftCertificateState = useAppSelector(selectGiftCertificate);

  // UPDATED: Better admin check pattern
  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const is_admin = useMemo(() => {
    const userGroups =
      getAdminSessionState.data?.admin?.user_details?.groups || [];
    return userGroups.some((group) => group.id === 1 || group.id === 20);
  }, [getAdminSessionState.data?.admin?.user_details?.groups]);
  const isAdminCheckComplete = getAdminSessionState.data !== undefined;

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedStores, setSelectedStores] = useState<number[]>([]);
  const [expandedGroup, setExpandedGroup] = useState<number | null>(null);
  const [storeSearchQuery, setStoreSearchQuery] = useState("");
  const [editingGroup, setEditingGroup] = useState<any>(null);
  const [groupToDelete, setGroupToDelete] = useState<any>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedGroupForMenu, setSelectedGroupForMenu] = useState<any>(null);

  // Search query for stores within each group
  const [groupStoreSearch, setGroupStoreSearch] = useState<{
    [key: number]: string;
  }>({});

  const { dropdownData, storeGroups } = giftCertificateState;

  useEffect(() => {
    dispatch(getAdminSession());
  }, [dispatch]);

  useEffect(() => {
    // Only load data after admin check is complete and user is admin
    if (isAdminCheckComplete && is_admin) {
      dispatch(getDropdownData());
      dispatch(getStoreGroupsDetailed());
    }
  }, [dispatch, isAdminCheckComplete, is_admin]);

  // Function to get filtered stores for a specific group
  const getFilteredGroupStores = (group: any) => {
    if (!group.stores) return [];

    const searchQuery = groupStoreSearch[group.id] || "";
    if (!searchQuery.trim()) return group.stores;

    return group.stores.filter(
      (store: any) =>
        store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        store.address.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Function to handle group store search
  const handleGroupStoreSearch = (groupId: number, query: string) => {
    setGroupStoreSearch((prev) => ({
      ...prev,
      [groupId]: query,
    }));
  };

  // UPDATED: Filter stores - now only active stores are returned from backend
  const filteredStores = useMemo(() => {
    if (!dropdownData?.stores) return [];

    if (!storeSearchQuery.trim()) {
      return dropdownData.stores; // All stores are already active (status = 1) from backend
    }

    return dropdownData.stores.filter(
      (store) =>
        store.name.toLowerCase().includes(storeSearchQuery.toLowerCase()) ||
        store.address.toLowerCase().includes(storeSearchQuery.toLowerCase())
    );
  }, [dropdownData?.stores, storeSearchQuery]);

  // All existing handler functions remain the same
  const handleStoreToggle = (storeId: number) => {
    setSelectedStores((prev) =>
      prev.includes(storeId)
        ? prev.filter((id) => id !== storeId)
        : [...prev, storeId]
    );
  };

  const handleSelectAll = () => {
    if (!filteredStores) return;

    const filteredStoreIds = filteredStores.map((store) => store.id);

    if (filteredStoreIds.every((id) => selectedStores.includes(id))) {
      setSelectedStores((prev) =>
        prev.filter((id) => !filteredStoreIds.includes(id))
      );
    } else {
      setSelectedStores((prev) =>
        Array.from(new Set([...prev, ...filteredStoreIds]))
      );
    }
  };

  const handleCreateGroup = () => {
    if (!groupName.trim() || selectedStores.length === 0) return;

    dispatch(
      createStoreGroup({
        group_name: groupName,
        store_ids: selectedStores,
      })
    ).then(() => {
      setCreateDialogOpen(false);
      setGroupName("");
      setSelectedStores([]);
      setStoreSearchQuery("");
      dispatch(getStoreGroupsDetailed());
    });
  };

  const handleEditGroup = (group: any) => {
    setEditingGroup(group);
    setGroupName(group.group_name);
    setSelectedStores(
      group.stores ? group.stores.map((store: any) => store.id) : []
    );
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleUpdateGroup = () => {
    if (!editingGroup || !groupName.trim() || selectedStores.length === 0)
      return;

    dispatch(
      updateStoreGroup({
        group_id: editingGroup.id,
        group_name: groupName,
        store_ids: selectedStores,
      })
    ).then(() => {
      setEditDialogOpen(false);
      setEditingGroup(null);
      setGroupName("");
      setSelectedStores([]);
      setStoreSearchQuery("");
      dispatch(getStoreGroupsDetailed());
    });
  };

  const handleDeleteClick = (group: any) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteGroup = () => {
    if (!groupToDelete) return;

    dispatch(deleteStoreGroup(groupToDelete.id)).then(() => {
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
      dispatch(getStoreGroupsDetailed());
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, group: any) => {
    setMenuAnchor(event.currentTarget);
    setSelectedGroupForMenu(group);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedGroupForMenu(null);
  };

  const resetDialogState = () => {
    setGroupName("");
    setSelectedStores([]);
    setStoreSearchQuery("");
    setEditingGroup(null);
  };

  useEffect(() => {
    if (!createDialogOpen && !editDialogOpen) {
      resetDialogState();
    }
  }, [createDialogOpen, editDialogOpen]);

  // Show loading screen while checking admin status
  if (!isAdminCheckComplete) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Card>
          <CardContent className="text-center py-12">
            <CircularProgress size={40} className="mb-4" />
            <Typography variant="h6" className="text-gray-600">
              Checking permissions...
            </Typography>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show access denied for non-admins
  if (!is_admin) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Card>
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaLock className="text-red-500" size={24} />
            </div>
            <Typography variant="h5" className="text-gray-800 mb-2">
              Access Denied
            </Typography>
            <Typography variant="body1" className="text-gray-600 mb-4">
              You don't have permission to access Store Groups Management.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              This feature is only available to administrators.
            </Typography>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <FaBuilding className="text-white" size={20} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Store Groups Management
              </h1>
              <p className="text-gray-600">
                Create and manage store groups for sponsorship gift certificates
                (Active stores only)
              </p>
            </div>
          </div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FaPlus />}
            onClick={() => setCreateDialogOpen(true)}
            className="bg-red-600 hover:bg-red-700"
          >
            CREATE STORE GROUP
          </Button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {giftCertificateState.status === GiftCertificateState.success && (
        <Alert
          severity="success"
          className="mb-4"
          onClose={() => dispatch(resetGiftCertificate())}
        >
          {giftCertificateState.message}
        </Alert>
      )}

      {giftCertificateState.status === GiftCertificateState.fail && (
        <Alert
          severity="error"
          className="mb-4"
          onClose={() => dispatch(resetGiftCertificate())}
        >
          {giftCertificateState.message}
        </Alert>
      )}

      {/* Store Groups List */}
      <div className="space-y-3">
        {storeGroups.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <FaBuilding size={48} className="mx-auto text-gray-400 mb-4" />
              <Typography variant="h6" className="text-gray-600 mb-2">
                No Store Groups Found
              </Typography>
              <Typography variant="body2" className="text-gray-500 mb-4">
                Create your first store group to organize active stores for
                sponsorship gift certificates
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FaPlus />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create Store Group
              </Button>
            </CardContent>
          </Card>
        ) : (
          storeGroups.map((group) => {
            const filteredGroupStores = getFilteredGroupStores(group);

            return (
              <Card
                key={group.id}
                className="hover:shadow-md transition-shadow"
              >
                <Accordion
                  expanded={expandedGroup === group.id}
                  onChange={() =>
                    setExpandedGroup(
                      expandedGroup === group.id ? null : group.id
                    )
                  }
                >
                  <AccordionSummary
                    expandIcon={<MdExpandMore />}
                    className="py-2"
                  >
                    <div className="flex items-center justify-between w-full mr-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <FaBuilding className="text-purple-600" size={14} />
                        </div>
                        <div>
                          <Typography
                            variant="h6"
                            className="font-semibold text-base"
                          >
                            {group.group_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            className="text-sm"
                          >
                            {group.store_count} active stores • Created by{" "}
                            {group.first_name} {group.last_name}
                          </Typography>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Chip
                          label={`${group.store_count} active stores`}
                          size="small"
                          color="primary"
                          className="text-xs"
                        />
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className="hidden sm:block"
                        >
                          {new Date(group.created_at).toLocaleDateString()}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, group);
                          }}
                        >
                          <FaEllipsisV size={12} />
                        </IconButton>
                      </div>
                    </div>
                  </AccordionSummary>

                  <AccordionDetails className="pt-0">
                    <Divider className="mb-3" />
                    {group.stores && group.stores.length > 0 ? (
                      <div>
                        {/* Header with inline search */}
                        <div className="flex items-center justify-between mb-3">
                          <Typography
                            variant="subtitle1"
                            className="font-medium flex items-center text-sm"
                          >
                            <FaStore className="mr-2" size={14} />
                            Active Stores in Group ({group.stores.length})
                          </Typography>

                          {/* Inline search for group stores */}
                          <TextField
                            size="small"
                            placeholder="Search stores..."
                            value={groupStoreSearch[group.id] || ""}
                            onChange={(e) =>
                              handleGroupStoreSearch(group.id, e.target.value)
                            }
                            className="w-64"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <FaSearch
                                    size={14}
                                    className="text-gray-400"
                                  />
                                </InputAdornment>
                              ),
                              endAdornment: groupStoreSearch[group.id] && (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleGroupStoreSearch(group.id, "")
                                    }
                                  >
                                    <FaTimes size={12} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </div>

                        {/* Store list */}
                        {filteredGroupStores.length > 0 ? (
                          <div className="border rounded-lg overflow-hidden">
                            <List
                              dense
                              className="p-0 max-h-80 overflow-y-auto"
                            >
                              {filteredGroupStores.map(
                                (store: any, index: number) => (
                                  <React.Fragment key={store.id}>
                                    <ListItem className="py-2 px-3 hover:bg-gray-50">
                                      <ListItemIcon className="min-w-0 mr-3">
                                        <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center">
                                          <FaStore
                                            className="text-green-600"
                                            size={10}
                                          />
                                        </div>
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={
                                          <Typography
                                            variant="body2"
                                            className="font-medium text-sm"
                                          >
                                            {store.name}
                                          </Typography>
                                        }
                                        secondary={
                                          <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            className="text-xs"
                                          >
                                            {store.address}
                                          </Typography>
                                        }
                                      />
                                      <div className="flex items-center space-x-2">
                                        <Chip
                                          label="Active"
                                          size="small"
                                          color="success"
                                          className="text-xs"
                                        />
                                        <Chip
                                          label={`ID: ${store.id}`}
                                          size="small"
                                          variant="outlined"
                                          className="text-xs"
                                        />
                                      </div>
                                    </ListItem>
                                    {index < filteredGroupStores.length - 1 && (
                                      <Divider />
                                    )}
                                  </React.Fragment>
                                )
                              )}
                            </List>

                            {/* Footer with count */}
                            <div className="bg-gray-50 px-3 py-2 border-t">
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {filteredGroupStores.length ===
                                group.stores.length
                                  ? `Showing all ${group.stores.length} active stores`
                                  : `Showing ${filteredGroupStores.length} of ${group.stores.length} active stores`}
                              </Typography>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 border rounded-lg bg-gray-50">
                            <FaSearch
                              size={24}
                              className="mx-auto text-gray-400 mb-2"
                            />
                            <Typography variant="body2" color="textSecondary">
                              No active stores match your search "
                              {groupStoreSearch[group.id]}"
                            </Typography>
                            <Button
                              size="small"
                              onClick={() =>
                                handleGroupStoreSearch(group.id, "")
                              }
                              className="mt-2"
                            >
                              Clear Search
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-6 border rounded-lg bg-gray-50">
                        <FaStore
                          size={28}
                          className="mx-auto text-gray-400 mb-2"
                        />
                        <Typography variant="body2" color="textSecondary">
                          No active stores found in this group
                        </Typography>
                      </div>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Card>
            );
          })
        )}
      </div>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditGroup(selectedGroupForMenu)}>
          <FaEdit className="mr-2" size={14} />
          Edit Group
        </MenuItem>
        <MenuItem onClick={() => handleDeleteClick(selectedGroupForMenu)}>
          <FaTrash className="mr-2" size={14} />
          Delete Group
        </MenuItem>
      </Menu>

      {/* Create Store Group Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Store Group</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-4">
            <TextField
              fullWidth
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter store group name"
              required
            />

            {/* Store Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Typography variant="h6">Select Active Stores</Typography>
                <div className="flex items-center space-x-2">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSelectAll}
                    startIcon={<FaExpandArrowsAlt />}
                  >
                    {selectedStores.length === dropdownData?.stores?.length
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                  <Chip
                    label={`${selectedStores.length} selected`}
                    size="small"
                    color={selectedStores.length > 0 ? "primary" : "default"}
                  />
                </div>
              </div>

              <TextField
                fullWidth
                size="small"
                placeholder="Search active stores by name or address..."
                value={storeSearchQuery}
                onChange={(e) => setStoreSearchQuery(e.target.value)}
                className="mb-3"
                InputProps={{
                  startAdornment: (
                    <FaSearch className="text-gray-400 mr-2" size={16} />
                  ),
                }}
              />

              <div className="border-2 border-gray-200 rounded-lg bg-gray-50">
                <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
                  <Typography
                    variant="body2"
                    className="font-medium text-gray-700"
                  >
                    Available Active Stores ({filteredStores.length})
                  </Typography>
                </div>

                <div className="max-h-96 overflow-y-auto bg-white">
                  {filteredStores.length === 0 ? (
                    <div className="p-8 text-center">
                      <Typography variant="body2" color="textSecondary">
                        No active stores found
                      </Typography>
                    </div>
                  ) : (
                    <List dense className="p-0">
                      {filteredStores.map((store, index) => (
                        <React.Fragment key={store.id}>
                          <ListItem
                            button
                            onClick={() => handleStoreToggle(store.id)}
                            className="hover:bg-blue-50 transition-colors px-4 py-3"
                          >
                            <Checkbox
                              checked={selectedStores.includes(store.id)}
                              tabIndex={-1}
                              disableRipple
                              size="small"
                              color="primary"
                            />
                            <ListItemText
                              primary={
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm text-gray-900">
                                    {store.name}
                                  </span>
                                  <div className="flex items-center space-x-2">
                                    <Chip
                                      label="Active"
                                      size="small"
                                      color="success"
                                      className="text-xs"
                                    />
                                    {selectedStores.includes(store.id) && (
                                      <Chip
                                        label="Selected"
                                        size="small"
                                        color="primary"
                                        className="text-xs"
                                      />
                                    )}
                                  </div>
                                </div>
                              }
                              secondary={
                                <span className="text-xs text-gray-600 mt-1 block">
                                  {store.address}
                                </span>
                              }
                            />
                          </ListItem>
                          {index < filteredStores.length - 1 && (
                            <Divider className="mx-4" />
                          )}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </div>
              </div>

              {/* Selected Stores Summary */}
              {selectedStores.length > 0 && (
                <Card className="mt-3 bg-blue-50 border border-blue-200">
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between mb-2">
                      <Typography
                        variant="subtitle2"
                        className="text-blue-800 flex items-center"
                      >
                        <FaStore className="mr-1" size={14} />
                        Selected Active Stores ({selectedStores.length})
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        onClick={() => setSelectedStores([])}
                        className="text-xs"
                      >
                        Clear All
                      </Button>
                    </div>

                    <div className="max-h-32 overflow-y-auto">
                      <div className="grid grid-cols-1 gap-1">
                        {selectedStores.map((storeId) => {
                          const store = dropdownData?.stores?.find(
                            (s) => s.id === storeId
                          );
                          return store ? (
                            <div
                              key={storeId}
                              className="flex items-center justify-between p-2 hover:bg-blue-100 rounded border bg-white"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">
                                  {store.name}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {store.address}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Chip
                                  label="Active"
                                  size="small"
                                  color="success"
                                  className="text-xs"
                                />
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleStoreToggle(storeId)}
                                  className="flex-shrink-0"
                                >
                                  <FaTrash size={12} />
                                </IconButton>
                              </div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateGroup}
            variant="contained"
            disabled={!groupName.trim() || selectedStores.length === 0}
          >
            Create Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Store Group Dialog - Similar to create but with update functionality */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Store Group</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-4">
            <TextField
              fullWidth
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter store group name"
              required
            />

            {/* Same store selection UI as create dialog */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Typography variant="h6">Select Active Stores</Typography>
                <div className="flex items-center space-x-2">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSelectAll}
                    startIcon={<FaExpandArrowsAlt />}
                  >
                    {selectedStores.length === dropdownData?.stores?.length
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                  <Chip
                    label={`${selectedStores.length} selected`}
                    size="small"
                    color={selectedStores.length > 0 ? "primary" : "default"}
                  />
                </div>
              </div>

              <TextField
                fullWidth
                size="small"
                placeholder="Search active stores by name or address..."
                value={storeSearchQuery}
                onChange={(e) => setStoreSearchQuery(e.target.value)}
                className="mb-3"
                InputProps={{
                  startAdornment: (
                    <FaSearch className="text-gray-400 mr-2" size={16} />
                  ),
                }}
              />

              <div className="border-2 border-gray-200 rounded-lg bg-gray-50">
                <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
                  <Typography
                    variant="body2"
                    className="font-medium text-gray-700"
                  >
                    Available Active Stores ({filteredStores.length})
                  </Typography>
                </div>

                <div className="max-h-96 overflow-y-auto bg-white">
                  {filteredStores.length === 0 ? (
                    <div className="p-8 text-center">
                      <Typography variant="body2" color="textSecondary">
                        No active stores found
                      </Typography>
                    </div>
                  ) : (
                    <List dense className="p-0">
                      {filteredStores.map((store, index) => (
                        <React.Fragment key={store.id}>
                          <ListItem
                            button
                            onClick={() => handleStoreToggle(store.id)}
                            className="hover:bg-blue-50 transition-colors px-4 py-3"
                          >
                            <Checkbox
                              checked={selectedStores.includes(store.id)}
                              tabIndex={-1}
                              disableRipple
                              size="small"
                              color="primary"
                            />
                            <ListItemText
                              primary={
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm text-gray-900">
                                    {store.name}
                                  </span>
                                  <div className="flex items-center space-x-2">
                                    <Chip
                                      label="Active"
                                      size="small"
                                      color="success"
                                      className="text-xs"
                                    />
                                    {selectedStores.includes(store.id) && (
                                      <Chip
                                        label="Selected"
                                        size="small"
                                        color="primary"
                                        className="text-xs"
                                      />
                                    )}
                                  </div>
                                </div>
                              }
                              secondary={
                                <span className="text-xs text-gray-600 mt-1 block">
                                  {store.address}
                                </span>
                              }
                            />
                          </ListItem>
                          {index < filteredStores.length - 1 && (
                            <Divider className="mx-4" />
                          )}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </div>
              </div>

              {/* Selected Stores Summary */}
              {selectedStores.length > 0 && (
                <Card className="mt-3 bg-blue-50 border border-blue-200">
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between mb-2">
                      <Typography
                        variant="subtitle2"
                        className="text-blue-800 flex items-center"
                      >
                        <FaStore className="mr-1" size={14} />
                        Selected Active Stores ({selectedStores.length})
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        onClick={() => setSelectedStores([])}
                        className="text-xs"
                      >
                        Clear All
                      </Button>
                    </div>

                    <div className="max-h-32 overflow-y-auto">
                      <div className="grid grid-cols-1 gap-1">
                        {selectedStores.map((storeId) => {
                          const store = dropdownData?.stores?.find(
                            (s) => s.id === storeId
                          );
                          return store ? (
                            <div
                              key={storeId}
                              className="flex items-center justify-between p-2 hover:bg-blue-100 rounded border bg-white"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">
                                  {store.name}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {store.address}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Chip
                                  label="Active"
                                  size="small"
                                  color="success"
                                  className="text-xs"
                                />
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleStoreToggle(storeId)}
                                  className="flex-shrink-0"
                                >
                                  <FaTrash size={12} />
                                </IconButton>
                              </div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateGroup}
            variant="contained"
            disabled={!groupName.trim() || selectedStores.length === 0}
          >
            Update Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaTrash className="text-red-500" />
            <span>Delete Store Group</span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2">
            <Alert severity="warning" className="mb-4">
              <Typography variant="body2">
                <strong>Warning:</strong> This action cannot be undone. The
                store group will be permanently deleted.
              </Typography>
            </Alert>

            {groupToDelete && (
              <Card className="bg-gray-50">
                <CardContent>
                  <Typography variant="h6" className="mb-2">
                    Group Details
                  </Typography>
                  <div className="space-y-2">
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        Group Name
                      </Typography>
                      <Typography variant="body2" className="font-semibold">
                        {groupToDelete.group_name}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        Number of Active Stores
                      </Typography>
                      <Typography variant="body2">
                        {groupToDelete.store_count} active stores
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        Created By
                      </Typography>
                      <Typography variant="body2">
                        {groupToDelete.first_name} {groupToDelete.last_name}
                      </Typography>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteGroup}
            variant="contained"
            color="error"
            startIcon={<FaTrash />}
          >
            Delete Group
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
